/**
 * Custom hook for managing Dietician Registration Form state and logic
 *
 * Extracts form state management, validation logic, and event handlers
 * from the DieticianRegistrationForm component for better separation of concerns.
 *
 * @module hooks/useDieticianRegistrationFormState
 */

import { useState, useCallback, useRef } from 'react';
import { useDieticianValidation } from '@/hooks/useDieticianValidation';
import { scrollToFirstErrorField } from '@/utils/scrollToError';

// Define field order for validation and scrolling
const FORM_FIELDS = ['firstName', 'lastName', 'email', 'password', 'confirmPassword'] as const;

export interface DieticianRegistrationFormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
}

export interface UseDieticianRegistrationFormStateProps {
  initialData?: Partial<DieticianRegistrationFormData>;
  onSubmit: (data: DieticianRegistrationFormData) => Promise<void>;
}

export interface UseDieticianRegistrationFormStateReturn {
  // Form state
  formData: DieticianRegistrationFormData;
  fieldErrors: Record<string, string>;
  fieldRefs: React.RefObject<Record<string, HTMLInputElement | null>>;

  // Event handlers
  handleFieldChange: (fieldName: string, value: string) => void;
  handleFieldBlur: (fieldName: string) => void;
  handleSubmit: (event: React.FormEvent) => void;
}

export function useDieticianRegistrationFormState({
  initialData,
  onSubmit
}: UseDieticianRegistrationFormStateProps): UseDieticianRegistrationFormStateReturn {
  const validation = useDieticianValidation();

  // Field refs for scroll-to-error functionality
  const fieldRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Form state
  const [formData, setFormData] = useState<DieticianRegistrationFormData>({
    email: initialData?.email || '',
    password: initialData?.password || '',
    confirmPassword: '',
    firstName: initialData?.firstName || '',
    lastName: initialData?.lastName || ''
  });

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Validation functions
  const validateConfirmPassword = useCallback((confirmPassword: string, password: string) => {
    if (confirmPassword !== password) {
      setFieldErrors(prev => ({
        ...prev,
        confirmPassword: 'Passwords do not match'
      }));
    } else {
      setFieldErrors(prev => ({
        ...prev,
        confirmPassword: ''
      }));
    }
  }, []);

  const validateField = useCallback((fieldName: string, value: string) => {
    switch (fieldName) {
      case 'email': {
        const emailResult = validation.validateEmail(value);
        setFieldErrors(prev => ({
          ...prev,
          [fieldName]: emailResult.isValid ? '' : emailResult.error || ''
        }));
        break;
      }

      case 'password': {
        const passwordResult = validation.validatePassword(value);
        setFieldErrors(prev => ({
          ...prev,
          [fieldName]: passwordResult.isValid ? '' : passwordResult.error || ''
        }));

        // Also validate confirm password if it's been touched
        if (touchedFields.has('confirmPassword')) {
          validateConfirmPassword(formData.confirmPassword, value);
        }
        break;
      }

      case 'confirmPassword':
        validateConfirmPassword(value, formData.password);
        break;

      case 'firstName':
      case 'lastName': {
        const nameResult = validation.validateName(
          value,
          fieldName === 'firstName' ? 'First name' : 'Last name'
        );
        setFieldErrors(prev => ({
          ...prev,
          [fieldName]: nameResult.isValid ? '' : nameResult.error || ''
        }));
        break;
      }
    }
  }, [validation, formData.password, formData.confirmPassword, touchedFields, validateConfirmPassword]);

  // Event handlers
  const handleFieldChange = useCallback((fieldName: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Validate if field has been touched
    if (touchedFields.has(fieldName)) {
      validateField(fieldName, value);
    }
  }, [touchedFields, validateField]);

  const handleFieldBlur = useCallback((fieldName: string) => {
    setTouchedFields(prev => new Set([...prev, fieldName]));
    validateField(fieldName, formData[fieldName as keyof DieticianRegistrationFormData]);
  }, [formData, validateField]);

  // Form submission
  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();

    // Mark all fields as touched
    setTouchedFields(new Set(FORM_FIELDS));

    // Validate all fields and collect results
    const validationResults: { field: string; isValid: boolean; error?: string }[] = [];
    const newFieldErrors: Record<string, string> = {};

    FORM_FIELDS.forEach(fieldName => {
      const value = formData[fieldName as keyof DieticianRegistrationFormData];
      let result: { isValid: boolean; error?: string };

      switch (fieldName) {
        case 'email': {
          result = validation.validateEmail(value);
          break;
        }
        case 'password': {
          result = validation.validatePassword(value);
          break;
        }
        case 'confirmPassword': {
          result = value !== formData.password
            ? { isValid: false, error: 'Passwords do not match' }
            : { isValid: true };
          break;
        }
        case 'firstName':
        case 'lastName': {
          result = validation.validateName(
            value,
            fieldName === 'firstName' ? 'First name' : 'Last name'
          );
          break;
        }
        default: {
          result = { isValid: true };
          break;
        }
      }

      validationResults.push({ field: fieldName, ...result });
      newFieldErrors[fieldName] = result.isValid ? '' : (result.error || '');
    });

    // Update field errors state
    setFieldErrors(newFieldErrors);

    const isFormValid = validationResults.every(result => result.isValid);

    if (!isFormValid) {
      // Create immediate validation state for scroll-to-error
      const immediateValidationState: Record<string, { touched?: boolean; result: { isValid: boolean; error?: string } }> = {};

      FORM_FIELDS.forEach(field => {
        const validationResult = validationResults.find(r => r.field === field);
        immediateValidationState[field] = {
          touched: true,
          result: {
            isValid: validationResult?.isValid ?? true,
            error: validationResult?.error
          }
        };
      });

      // Use immediate validation state for scrolling
      setTimeout(() => {
        scrollToFirstErrorField([...FORM_FIELDS], immediateValidationState, fieldRefs.current);
      }, 100);
      return;
    }

    // Submit form
    await onSubmit(formData);
  }, [formData, validation, onSubmit]);

  return {
    // Form state
    formData,
    fieldErrors,
    fieldRefs,

    // Event handlers
    handleFieldChange,
    handleFieldBlur,
    handleSubmit
  };
}